import {compareResults, isBetterThan, parseResultStringToNumber} from "./ResultUtils";

export function mapDisciplineGroupsToName(group: string, shorten = false) {
    let translation = MAPPING[group]
    if (shorten || translation === undefined) {
        translation = MAPPING_SHORTEN[group]
    }
    return translation ? translation : group //fallback
}

function getUnitOfDisciplineGroup(group: string) {
    const unit = UNIT[group]
    return unit ? unit : ''
}

export function exportCompetitionAsHTML(competition: Competition): string {

    const disciplines = joinDisciplineGroups(JSON.parse(JSON.stringify(competition.disciplines)))
    const [men, women] = splitDisciplinesByGender(disciplines)

    return `
        <div>
            ${getTable(men, "Resultate TVL-Männer ")}
            ${getTable(women, "Resultate TVL-Frauen")}
        </div>
    `
}

export function exportCompetitionAsString(competition: Competition, onlyPB: boolean, showUnits: boolean): {
    active: string,
    hasActive: boolean,
    youngster: string,
    hasYoungster: boolean
} {
    const groupedByAthlete = joinResultsOfAthletes(JSON.parse(JSON.stringify(competition.disciplines)))
    const linesYoungsters = []
    const linesActive = []

    for (const athleteName of Object.keys(groupedByAthlete)) {
        let athleteResults = groupedByAthlete[athleteName]
        if (onlyPB) {
            athleteResults = athleteResults.filter((result: Result) => result.info.includes("PB"))
            if (athleteResults.length === 0) {
                //no PB result for this athlete; ignore it
                continue
            }
        }

        const renderResultInfo = (resultInfo: string) => (resultInfo && resultInfo.length > 0 ? ` (${resultInfo}) ` : "")
        const renderResultString = (result: Result, onlyPB: boolean, showUnits: boolean) => {
            let resultString = mapDisciplineGroupsToName(result.disciplineGroup, true)
            resultString += ` ${result.result}`
            if (showUnits) {
                resultString += getUnitOfDisciplineGroup(result.disciplineGroup)
            }
            if (!onlyPB) {
                resultString += ` ${renderResultInfo(result.info)}`
            }
            return resultString
        }

        //collect results of athlete. If we only collect PB's, we ignore the info field
        const resultString = athleteResults.map((result: Result) => renderResultString(result, onlyPB, showUnits)).join("/")
        const currentYear = (new Date()).getFullYear()
        const athleteVintage = parseInt(athleteResults[0].vintage)
        let line = `${athleteName}: ${resultString}`
        if ((currentYear - athleteVintage) < 16) {
            //Nachwuchs (younger than U18)
            linesYoungsters.push(line)
        } else {
            //Aktive
            linesActive.push(line)
        }
    }

    return {
        active: linesActive.sort().join("\n"),
        hasActive: linesActive.length > 0,
        youngster: linesYoungsters.sort().join("\n"),
        hasYoungster: linesYoungsters.length > 0
    }
}

function getTable(disciplines: DisciplineCollection, title: string): string {

    const lines = []

    for (const discipline in disciplines) {
        lines.push(...disciplines[discipline].sort(compareResults).map((result, index) => getTableLine(result, !index)))
    }

    const table = `
            <br>
            <p><strong>${title}</strong></p>
            <table>
                <colgroup>
                    <col width="120">
                    <col width="250">
                    <col width="80">
                    <col width="200">
                </colgroup>
                <tbody>
                    ${lines.join('\n')}
                </tbody>
            </table>`

    return table
}

function getTableLine(result: Result, isFirstOfDiscipline: boolean) {

    const discipline = isFirstOfDiscipline ? getCell(mapDisciplineGroupsToName(result.disciplineGroup)) : '<td></td>'
    const name = getCell(result.athleteName)
    const resultValue = getCell(result.result)
    const vintage = getCell(result.vintage)
    return `<tr>
                ${discipline}
                ${name}
                ${vintage}
                ${resultValue}
            </tr>`
}

function getCell(value: string, rowSpan?: string, className?: string): string {
    const classAttr = className ? `class="${className}"` : ''
    return `<td ${classAttr}>${value ? value : ''}</td>`
}

const getResultWithInfo = (result: Result): string => `${result.result}${result.info ? " (" + result.info + ")" : ''}`

function joinDisciplineGroups(disciplines: DisciplineCollection): DisciplineCollection {
    const allResults = Object.values(disciplines).flat(1)
    const groupedByGroup = groupBy(allResults, 'disciplineGroup')


    for (const group in groupedByGroup) {
        const groupedByAthlete = groupBy(groupedByGroup[group], 'athleteName')
        const joinedResults = Object.values(groupedByAthlete).map(list => {
            const result = list[0]
            result.resultValues = list.map(result => parseResultStringToNumber(result.result))
            result.result = list.map(result => getResultWithInfo(result)).join(', ')
            return result
        }).flat(1)
        groupedByGroup[group] = joinedResults
    }

    return groupedByGroup
}

/**
 * Creates an object where each key corresponds to an athlete. The value is a list of all its results.
 * We only keep the best result if an athlete have multiple results of the same disciplineGroup.
 * @param {DisicplineCollection} disciplines
 * @return {AthleteResultsCollection}
 */
function joinResultsOfAthletes(disciplines: DisciplineCollection): AthleteResultsCollection {
    const alLResults: Result[] = Object.values(disciplines).flat(1)
    const resultsPerAthlete: { [athleteName: string]: { [disciplineGroup: string]: Result } } = {}
    // we collect all results of an athlete.
    // We use the format {athleteName: {disciplineGroup: Result}} such that
    // - no athlete is twice in the dict
    // - for no athlete, no discipline group is twice in the dict
    for (const result of alLResults) {
        if (!(result.athleteName in resultsPerAthlete)) {
            resultsPerAthlete[result.athleteName] = {}
        }

        let betterResult = result
        // we check if the there is already a result for a same DisciplineGroup.
        //If so, we check if the new value is better and replace the value by the better result
        if (resultsPerAthlete[result.athleteName][result.disciplineGroup]) {
            const existingResult = resultsPerAthlete[result.athleteName][result.disciplineGroup]
            betterResult = isBetterThan(existingResult, result) ? existingResult : result
        }
        resultsPerAthlete[result.athleteName][result.disciplineGroup] = betterResult
    }

    const result: AthleteResultsCollection = {}
    // we map the dictionary to a simpler form: {athleteName: Result[]} since we have already filtered out same disciplineGroups
    // and don't need the dict in the dict anymore
    for (const athleteName of Object.keys(resultsPerAthlete)) {
        result[athleteName] = Object.values(resultsPerAthlete[athleteName])
    }

    return result
}

function splitDisciplinesByGender(disciplines: DisciplineCollection): DisciplineCollection[] {
    const men: DisciplineCollection = {}
    const women: DisciplineCollection = {}

    const addResult = (disciplineCollection: DisciplineCollection, disciplineName: string, result: Result) => {
        if (!disciplineCollection[disciplineName]) {
            disciplineCollection[disciplineName] = []
        }
        disciplineCollection[disciplineName].push(result)
    }

    Object.keys(disciplines).forEach(disciplineName => {
        disciplines[disciplineName].forEach(result => {
            if (isManCategory(result.disciplineFullName)) {
                addResult(men, disciplineName, result)
            } else {
                addResult(women, disciplineName, result)
            }
        })
    })

    return [men, women]


}

function isManCategory(disciplineName: string) {
    const manCategoryIndicators = ["Männer", "Mann", "Hommes", "Uomini", "Uomo", "MAN", "U10M", "U12M", "U14M", "U16M", "U18M", "U20M", "U23M"]
    return manCategoryIndicators.some(indicator => disciplineName.includes(indicator))
}

const MAPPING: { [name: string]: string } = {
    '60M': '60m',
    '60H': '60m Hürden',
    '80H': '80m Hürden',
    '100': '100m',
    '10H': '100m Hürden',
    '11H': '110m Hürden',
    '150': '150m',
    '200': '200m',
    '300': '300m',
    '400': '400m',
    '40H': '400m Hürden',
    '600': '600m',
    '800': '800m',
    '1K0': '1000m',
    '1K5': '1500m',
    '3K0': '3000m',
    '5K0': '5000m',
    'WEI': 'Weitsprung',
    'WEZ': 'Weitsprung Zone',
    'DRE': 'Dreisprung',
    'HOC': 'Hochsprung',
    'STA': 'Stabhochsprung',
    'KUG': 'Kugel',
    'SPE': 'Speer',
    'HAM': 'Hammer',
    'DIS': 'Diskus'
}

const MAPPING_SHORTEN: { [name: string]: string } = {
    '60M': '60m',
    '60H': '60mH',
    '80H': '80mH',
    '100': '100m',
    '10H': '100mH',
    '11H': '110mH',
    '150': '150m',
    '200': '200m',
    '300': '300m',
    '400': '400m',
    '40H': '400mH',
    '600': '600m',
    '800': '800m',
    '1K0': '1000m',
    '1K5': '1500m',
    '3K0': '3000m',
    '5K0': '5000m',
    'WEI': 'Weit',
    'WEZ': 'Weit Zone',
    'DRE': 'Drei',
    'HOC': 'Hoch',
    'STA': 'Stab',
    'KUG': 'Kugel',
    'SPE': 'Speer',
    'HAM': 'Hammer',
    'DIS': 'Diskus'
}

const UNIT: { [name: string]: string } = {
    '60M': 'sek',
    '60H': 'sek',
    '80H': 'sek',
    '100': 'sek',
    '10H': 'sek',
    '11H': 'sek',
    '150': 'sek',
    '200': 'sek',
    '300': 'sek',
    '400': 'sek',
    '40H': 'sek',
    '600': 'min',
    '800': 'min',
    '1K0': 'min',
    '1K5': 'min',
    '3K0': 'min',
    '5K0': 'min',
    'WEI': 'm',
    'WEZ': 'm',
    'DRE': 'm',
    'HOC': 'm',
    'STA': 'm',
    'KUG': 'm',
    'SPE': 'm',
    'HAM': 'm',
    'DIS': 'm'
}

function groupBy<Type>(list: Type[], key: string): { [key: string]: Type[] } {
    return list.reduce(function (rv, x) {
        // @ts-ignore
        (rv[x[key]] = rv[x[key]] || []).push(x);
        return rv;
    }, {});
}
